import { Alert, CircularProgress, Dialog, DialogContent, DialogTitle, Link } from "@mui/material";
import { DataGridPro, Toolbar } from "@mui/x-data-grid-pro";
import { uniq } from "lodash-es";
import { useState } from "react";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { uuidv4 } from "../../../utils/uuid";
import { ExportMenu } from "../../generic/components/export-menu";
import { SupabasePersonConflictService } from "../server/supabase-person-conflict-service";

import type { GridSlotProps } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const PersonConflictDataGrid: FC = () => {
  const [showConflicts, setShowConflicts] = useState(false);

  const {
    data: personConflictData,
    loading,
    error,
  } = useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data, error: loadError } = await new SupabasePersonConflictService(supabase).getAll(
      account_id,
      { order: [{ column: "account_id" }] },
    );
    if (loadError) throw loadError;

    if (data.length === 0) return null;

    const keys: string[] = uniq(data.flatMap((current) => Object.keys(current)));
    const rows = data.map((datum) => ({
      ...datum,
      id: uuidv4(), // datagrid requires an ID
      record: JSON.stringify(datum.record),
    }));
    return { rows, keys };
  }, []);

  if (loading) return <CircularProgress />;

  if (personConflictData === null) return null;

  if (error) return <Alert severity="warning">Unable to load person conflict data.</Alert>;

  return (
    <>
      <Alert severity="warning" sx={{ marginTop: 1 }}>
        Conflicts found in the last sync,{" "}
        <Link href="#" onClick={() => setShowConflicts(true)}>
          click here
        </Link>{" "}
        for more details.
      </Alert>
      <Dialog open={showConflicts} onClose={() => setShowConflicts(false)} fullWidth maxWidth="md">
        <DialogTitle>Conflicting Records</DialogTitle>
        <DialogContent>
          <DataGridPro
            sx={{ display: "flex" }}
            columns={columns}
            rows={personConflictData.rows}
            slots={{
              toolbar: ConflictToolbar,
            }}
            showToolbar
            slotProps={{ toolbar: { allColumns: true } as GridSlotProps["toolbar"] }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const ConflictToolbar: FC = () => (
  <Toolbar>
    <ExportMenu />
  </Toolbar>
);

const columns = [
  { field: "email", headerName: "email", flex: 0.5 },
  { field: "employee_id", headerName: "employee_id", flex: 0.5 },
  { field: "conflicting_field", headerName: "conflicting_field", flex: 0.5 },
  { field: "record", headerName: "record" },
  { field: "last_seen_at", headerName: "last_seen_at", flex: 1 },
];
